import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Check } from 'lucide-react';
import { createMercadoPagoPreference, getSubscriptionStatus } from '../services/api';

// Fallback si no se puede leer el precio de la cuenta (precio de alta nueva).
const DEFAULT_MONTHLY_PRICE = 20000;

interface SubscriptionExpiredModalProps {
  show: boolean;
  subscriptionStatus?: string; // 'trial_expired' o 'expired'
}

const SubscriptionExpiredModal: React.FC<SubscriptionExpiredModalProps> = ({ 
  show, 
  subscriptionStatus = 'trial_expired' 
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Precio real de la cuenta (grandfathered): se lee del backend. null = cargando.
  const [monthlyPrice, setMonthlyPrice] = useState<number | null>(null);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    getSubscriptionStatus()
      .then((s) => {
        if (!cancelled) {
          setMonthlyPrice(typeof s.monthly_price === 'number' ? s.monthly_price : DEFAULT_MONTHLY_PRICE);
        }
      })
      .catch(() => {
        if (!cancelled) setMonthlyPrice(DEFAULT_MONTHLY_PRICE);
      });
    return () => { cancelled = true; };
  }, [show]);

  if (!show) return null;

  // Detectar el tipo de bloqueo
  const isTrial = subscriptionStatus === 'trial_expired';
  
  // Configuración según el tipo
  const config = isTrial ? {
    iconBg: 'from-orange-400 to-red-500',
    iconPath: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
    title: 'Período de Prueba Finalizado',
    message: 'Tu período de prueba de 15 días ha terminado. Para continuar usando ConsultAR, activá tu suscripción.',
    benefitsTitle: 'Activá todos tus servicios',
    buttonText: 'Activar Suscripción',
    showWarning: false
  } : {
    iconBg: 'from-red-400 to-red-600',
    iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z',
    title: 'Acceso Suspendido',
    message: 'Tu suscripción a ConsultAR está vencida. Para recuperar el acceso, renová tu suscripción ahora.',
    benefitsTitle: 'Recuperá el acceso a todos tus servicios',
    buttonText: 'Renovar Suscripción',
    showWarning: true
  };

  const handleActivate = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await createMercadoPagoPreference();
      
      if (response.init_point) {
        // Redirigir a Mercado Pago
        window.location.href = response.init_point;
      } else {
        throw new Error('No se pudo generar el link de pago');
      }
    } catch (err: any) {
      console.error('Error al crear preferencia de MP:', err);
      setError(err.message || 'Error al procesar el pago. Por favor, intenta nuevamente.');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('isLoggedIn');
    window.dispatchEvent(new CustomEvent('userLogout'));
    navigate('/');
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200]" />
      
      {/* Modal Container - SIEMPRE CENTRADO CON ESPACIO PARA HEADER */}
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4 pt-20 sm:pt-4">
        {/* Modal Content - CON SCROLL INTERNO */}
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[calc(90vh-4rem)] sm:max-h-[95vh] overflow-y-auto animate-in fade-in zoom-in duration-300">
          <div className="p-6 sm:p-8">
            {/* Icono */}
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className={`w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${config.iconBg} rounded-full flex items-center justify-center`}>
                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={config.iconPath} />
                </svg>
              </div>
            </div>

            {/* Título */}
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-3 sm:mb-4">
              {config.title}
            </h2>

            {/* Mensaje */}
            <p className="text-center text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
              {config.message}
            </p>

            {/* Advertencia (solo para suscripción vencida) */}
            {config.showWarning && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2 sm:mr-3 flex-shrink-0 mt-0.5" />
                  <div className="text-xs sm:text-sm text-gray-700">
                    <p className="font-semibold text-red-700 mb-1">Estado de tu cuenta:</p>
                    <p>Tu ciclo de suscripción terminó. Tus datos están seguros, renová para continuar.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Beneficios — copy corto, sin lista */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                <p className="text-sm font-semibold text-gray-900">
                  {config.benefitsTitle}
                </p>
              </div>
            </div>

            {/* Precio */}
            <div className="text-center mb-4 sm:mb-6">
              <div className="inline-flex items-baseline">
                <span className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-500 via-cyan-500 to-green-500 bg-clip-text text-transparent">
                  {monthlyPrice !== null ? `$${monthlyPrice.toLocaleString('es-AR')}` : '$…'}
                </span>
                <span className="text-gray-600 ml-2 text-sm sm:text-base">/mes</span>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Sin permanencia • Cancelás cuando quieras
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            {/* Botones */}
            <div className="space-y-3">
              <button
                onClick={handleActivate}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 via-cyan-500 to-green-500 text-white py-3 sm:py-4 rounded-2xl font-bold text-base sm:text-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Procesando...
                  </span>
                ) : (
                  config.buttonText
                )}
              </button>
              
              <button
                onClick={handleLogout}
                className="w-full text-gray-600 hover:text-gray-900 py-2 sm:py-3 rounded-xl font-medium transition-colors text-sm sm:text-base"
              >
                Cerrar Sesión
              </button>
            </div>

            {/* Ayuda */}
            <p className="text-center text-xs text-gray-500 mt-4 sm:mt-6">
              ¿Tenés dudas? Escribinos por{' '}
              <a 
                href="https://wa.me/5493794080538?text=Hola,%20necesito%20ayuda%20con%20mi%20suscripción"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-700 font-medium"
              >
                WhatsApp
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscriptionExpiredModal;